import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { CompanyThresholds, DocumentTemplate } from '@/types/domain'

/* The two Super-admin surfaces Story 2.9 specifies and nothing has ever driven:
 * the reusable not-included / disclaimer templates (AC 2) and the three
 * configurable thresholds (AC 4).
 *
 * Both halves of the server have existed and been tested since migration 0032.
 * `POST/PUT/DELETE /api/admin/settings/templates` and
 * `GET/PUT /api/admin/settings/thresholds` were mounted, reachable and called by
 * nobody — and the templates tab was worse than absent: its form sheet took an
 * `onSubmit` that closed the dialog and threw the draft away, which is the
 * fourth time this codebase has shipped that exact shape (the case-participant
 * sheet and the add-cost modal are the two recorded in deferred-work.md). A
 * screen that discards a save silently is what this file removes.
 *
 * ── The read lives elsewhere, deliberately ─────────────────────────────────
 * `useDocumentTemplates` stays in features/directories/api/useDirectories.ts and
 * is not duplicated here: the quote's own QuoteBlocksCard consumes it to offer a
 * disclaimer for insertion, so two readers with two query keys would let the
 * quote form and this screen disagree about what templates exist. This file
 * writes, and invalidates that key.
 *
 * ── WHAT MUST NOT BE BUILT HERE ────────────────────────────────────────────
 * Editing a template must never change a quote that already carries its words.
 * Migration 0033 makes what lands on a quote a COPY of the body at insertion
 * time and says at length that there is deliberately no `template_id` on
 * `quotes` "to make the mistake with". So nothing in this file may grow a
 * mutation that reaches a quote, and nothing may start sending a template id
 * anywhere near one — a sent offer's wording is a record of what a client was
 * told, not a view of current settings.
 */

/** The templates key is the DIRECTORIES one, because that is where the read is
 *  declared. Repeated as a literal rather than imported to avoid a features/
 *  cross-import for four characters; if a third consumer appears it moves to
 *  lib/query.ts with the rest. */
const templateKey = ['settings', 'templates'] as const
const thresholdsKey = ['settings', 'thresholds'] as const

export interface TemplatePayload {
  /** `null` means the template is company-wide rather than one entity's. */
  entityId: string | null
  kind: DocumentTemplate['kind']
  label: string
  body: string
}

function useInvalidateTemplates() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: templateKey })
  }
}

export function useCreateTemplate() {
  const invalidate = useInvalidateTemplates()

  return useMutation<DocumentTemplate, Error, TemplatePayload>({
    mutationFn: (payload) =>
      api.post<DocumentTemplate>('/admin/settings/templates', payload),
    onSuccess: invalidate,
  })
}

/** Amends a template.
 *
 * The whole record is submitted rather than a patch, for the reason
 * `useUpdateEntity` gives: a nil-means-unchanged body makes "clear this field"
 * indistinguishable from "leave it alone". */
export function useUpdateTemplate() {
  const invalidate = useInvalidateTemplates()

  return useMutation<DocumentTemplate, Error, { id: string; payload: TemplatePayload }>({
    mutationFn: ({ id, payload }) =>
      api.put<DocumentTemplate>(`/admin/settings/templates/${id}`, payload),
    onSuccess: invalidate,
  })
}

/** Deletes a template.
 *
 * Safe to hard-delete, which is unusual in this codebase and worth saying why:
 * every other directory record is deactivated rather than deleted because
 * historical records reference it. A template has no such references BY DESIGN —
 * 0033 copies the body onto the quote — so removing one takes it out of the
 * picker and changes no document that ever quoted it. That is the whole payoff
 * of the copy, and it is the reason this is a DELETE rather than an is_active
 * flag. */
export function useDeleteTemplate() {
  const invalidate = useInvalidateTemplates()

  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/admin/settings/templates/${id}`),
    onSuccess: invalidate,
  })
}

/* ── The three thresholds (AC 4, FR-16, FR-52) ─────────────────────────────── */

/** The thresholds, with their defaults and whether anybody has saved them.
 *
 * `configured` is carried by the server rather than inferred here, and that is a
 * decision `Service.ThresholdsPanel` makes deliberately: comparing the values
 * against the defaults would report a founder who deliberately saved 3/2/3 as
 * having configured nothing, so the flag reflects whether a row exists. The
 * panel shows the defaults either way; what `configured` changes is whether it
 * says these ARE the defaults or these were CHOSEN. */
export function useCompanyThresholds() {
  return useQuery<CompanyThresholds>({
    queryKey: thresholdsKey,
    queryFn: async () => {
      if (USE_MOCKS) {
        return {
          followUpDays: 3,
          validityWarningDays: 2,
          stuckShipmentDays: 3,
          configured: false,
          defaultFollowUpDays: 3,
          defaultValidityWarningDays: 2,
          defaultStuckShipmentDays: 3,
        }
      }
      return api.get<CompanyThresholds>('/settings/thresholds')
    },
  })
}

export interface ThresholdsPayload {
  followUpDays: number
  validityWarningDays: number
  stuckShipmentDays: number
}

/** Saves all three thresholds together.
 *
 * One request for three numbers because the server writes the row whole
 * (`UpsertThresholds`), so a per-field save would read back two values it did
 * not send. The handler requires all three keys and refuses a partial body by
 * naming the missing field, which is why the panel submits every one of them
 * even when the reader changed only one. */
export function useSaveThresholds() {
  const queryClient = useQueryClient()

  return useMutation<CompanyThresholds, Error, ThresholdsPayload>({
    mutationFn: (payload) =>
      api.put<CompanyThresholds>('/admin/settings/thresholds', payload),
    onSuccess: (saved) => {
      // The response IS the panel's next state, defaults and `configured`
      // included, so it is seeded rather than refetched — the alternative shows
      // a "not configured" hint for one frame after the save that configured it.
      queryClient.setQueryData(thresholdsKey, saved)
    },
  })
}
